import sendClarityEvent from "utils/clarity"
import sendHotjarEvent from "utils/hotjar"
import { PAYWALL_FUNNEL_STEPS, paywallFunnelStep } from "utils/paywall-funnel"

// Analytics adapter for the session recorders (Hotjar, Clarity): the paywall
// funnel plus the incentive panel clicks. Everything else is ignored.
const RECORDED_EVENTS = [].concat(...PAYWALL_FUNNEL_STEPS, [
  "incentive_offer_click",
  "incentive_continue_click",
])

// Hotjar stores event timestamps with millisecond precision and no sequence
// counter, so events emitted within the same tick cannot be ordered by its
// funnels. The queue below spaces emissions by EVENT_GAP_MS to keep every
// event on its own millisecond and tick.
//
// Right after paywall_view the Hotjar tracker may (re)establish its websocket
// (that event triggers session capture). Until the socket is connected, each
// newly written event overwrites the previous unsent one, so after that
// event the queue holds back for CAPTURE_START_HOLD_MS once.
const EVENT_GAP_MS = 50
const CAPTURE_START_HOLD_MS = 2500

// One representative event per funnel step, used to backfill steps a user
// skipped (e.g. jumping straight to the payment step, or returning from
// Stripe into a fresh Hotjar session) so the funnel never loses the session.
// These placeholders go to the recorders only, never to GTM or Sentry.
const STEP_BACKFILL_EVENTS = [
  "paywall_view",
  "paywall_engagement_backfill",
  "begin_checkout",
  "add_payment_info",
  "purchase",
]

const queue = []
const reachedSteps = new Set()
let notBefore = 0
let holdApplied = false
let timerId = null

// The snippets define window.hj / window.clarity, and the layouts render them
// only while the hotjar / microsoft_clarity feature flags are on. Without a
// recorder nothing is queued and no timer runs.
function recorderLoaded() {
  return typeof window.hj === "function" || typeof window.clarity === "function"
}

function processQueue() {
  timerId = null
  if (!queue.length) return

  const now = Date.now()
  if (now < notBefore) {
    timerId = setTimeout(processQueue, notBefore - now)
    return
  }

  const event = queue.shift()
  sendHotjarEvent(event)
  sendClarityEvent(event)

  if (event === "paywall_view" && !holdApplied) {
    holdApplied = true
    notBefore = now + CAPTURE_START_HOLD_MS
  } else {
    notBefore = now + EVENT_GAP_MS
  }

  if (queue.length) {
    timerId = setTimeout(processQueue, Math.max(notBefore - Date.now(), 0))
  }
}

function schedule(delay) {
  if (timerId !== null) return
  timerId = setTimeout(processQueue, Math.max(delay, 0))
}

function queueEvent(event, step) {
  if (step !== -1) reachedSteps.add(step)
  queue.push(event)
  schedule(0)
}

// Drops the remaining hold so pending events drain before the page unloads.
function flush() {
  notBefore = 0
  if (timerId !== null) {
    clearTimeout(timerId)
    timerId = null
  }
  schedule(0)
}

export default function recorderAnalyticsAdapter(event) {
  if (!RECORDED_EVENTS.includes(event) || !recorderLoaded()) return

  const step = paywallFunnelStep(event)
  for (let i = 0; i < step; i += 1) {
    if (!reachedSteps.has(i)) queueEvent(STEP_BACKFILL_EVENTS[i], i)
  }
  queueEvent(event, step)

  // add_payment_info = Checkout Session created; the Stripe redirect follows.
  if (event === "add_payment_info") flush()
}
